import {
  Box,
  Table,
  Text,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  BoxProps,
  Icon,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import {
  RiCheckboxBlankCircleLine,
  RiCheckboxCircleLine,
  RiEyeLine,
} from 'react-icons/ri';
import { LinkButton } from '../../../../../../../components/LinkButton';
import { Pagination } from '../../../../../../../components/Pagination';
import { TableFilters } from '../../../../../../../components/TableFilters';
import { IActivityListItem } from '../../../../../../../services/Activities/ListActivitiesService';

export interface IActivity extends IActivityListItem {
  formattedCreatedAt: string;
}

interface IActivityTableProps extends BoxProps {
  activities: IActivity[];
  currentPage?: number;
  filterBy?: string;
  onChangeFilterBy?: (value: string) => void;
  onFilter?: (value: string) => void;
  onOrder?: (order: 'ASC' | 'DESC') => void;
  onPageChange?: (page: number) => void;
  onSort?: (sort: 'name' | 'isActive' | 'createdAt') => void;
  order?: 'ASC' | 'DESC';
  sortBy?: string;
  totalPages?: number;
}

export const ActivityTable = ({
  activities,
  currentPage,
  filterBy,
  onChangeFilterBy,
  onFilter,
  onOrder,
  onPageChange,
  onSort,
  order,
  sortBy,
  totalPages,
  ...rest
}: IActivityTableProps): JSX.Element => {
  const filterByOptions = useMemo(
    () => [
      {
        label: 'Name',
        value: 'name',
      },
    ],
    [],
  );

  const sortByOptions = useMemo(
    () => [
      {
        label: 'Name',
        value: 'name',
      },
      {
        label: 'Active',
        value: 'isActive',
      },
      {
        label: 'Registration date',
        value: 'createdAt',
      },
    ],
    [],
  );

  return (
    <Box {...rest}>
      <TableFilters
        filterBy={filterBy}
        filterByOptions={filterByOptions}
        onChangeFilterBy={onChangeFilterBy}
        onFilter={onFilter}
        onOrder={onOrder}
        onSort={(sort) =>
          !!onSort && onSort(sort as 'name' | 'isActive' | 'createdAt')
        }
        order={order}
        sortBy={sortBy}
        sortByOptions={sortByOptions}
      />

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            <Th>Calendar</Th>

            <Th>Tenant</Th>

            <Th>Resource</Th>

            <Th textAlign="center">Active</Th>

            <Th textAlign="center">Registration date</Th>

            <Th textAlign="center" w="8" />
          </Tr>
        </Thead>

        <Tbody>
          {activities.map((activity) => (
            <Tr key={activity.id}>
              <Td>
                <Box>
                  <Text fontWeight="bold">{activity.name}</Text>
                  <Text fontSize="sm" color="gray.500">
                    {activity.spot.name}
                  </Text>
                </Box>
              </Td>

              <Td>{activity.spot.venture.name}</Td>

              <Td>{activity.spot.name}</Td>

              <Td textAlign="center">
                {activity.isActive ? (
                  <Icon as={RiCheckboxCircleLine} color="green" />
                ) : (
                  <Icon as={RiCheckboxBlankCircleLine} color="gray.400" />
                )}
              </Td>

              <Td textAlign="center">{activity.formattedCreatedAt}</Td>

              <Td textAlign="center">
                <LinkButton
                  px={0}
                  to={{
                    pathname: '/activities/details',
                    state: {
                      activityId: activity.id,
                    },
                  }}
                >
                  <Icon as={RiEyeLine} fontSize="16" />
                </LinkButton>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!!currentPage && !!onPageChange && !!totalPages && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </Box>
  );
};
